/** Session-scoped decoded master cache — avoid re-fetching the same master URL. */

export type CachedMasterImage = {
  width: number
  height: number
}

const masterDecodedCache = new Map<string, CachedMasterImage>()

export function getCachedMaster(url: string): CachedMasterImage | null {
  const key = (url || '').trim()
  if (!key) return null
  return masterDecodedCache.get(key) || null
}

export function rememberCachedMaster(url: string, width: number, height: number): void {
  const key = (url || '').trim()
  if (!key || width <= 0 || height <= 0) return
  masterDecodedCache.set(key, { width, height })
}

export function clearCachedMaster(url?: string): void {
  if (!url) {
    masterDecodedCache.clear()
    return
  }
  masterDecodedCache.delete(url.trim())
}

/** Prefetch + decode master into browser cache / our session map. */
export function loadMasterImage(url: string): Promise<CachedMasterImage> {
  const key = url.trim()
  const hit = getCachedMaster(key)
  if (hit) return Promise.resolve(hit)
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.decoding = 'async'
    img.onload = () => {
      const entry = { width: img.naturalWidth, height: img.naturalHeight }
      rememberCachedMaster(key, entry.width, entry.height)
      resolve(entry)
    }
    img.onerror = () => reject(new Error('master_load_failed'))
    img.src = key
  })
}
